import { Chip, type ChipProps } from '@mui/material';
import { colors, radius, fonts, spacing } from '@/theme/tokens';

type AppPillVariant = 'default' | 'success' | 'warning' | 'danger' | 'accent';
type AppPillSize = 'sm' | 'md';

interface AppPillProps extends Omit<ChipProps, 'variant' | 'size'> {
  variant?: AppPillVariant;
  size?: AppPillSize;
}

const variantStyles = {
  default: {
    backgroundColor: colors.surfaceRaised,
    color: colors.textMuted,
    borderColor: colors.border,
  },
  success: {
    backgroundColor: 'rgba(60, 203, 127, 0.15)',
    color: colors.success,
    borderColor: 'transparent',
  },
  warning: {
    backgroundColor: 'rgba(242, 184, 75, 0.15)',
    color: colors.warning,
    borderColor: 'transparent',
  },
  danger: {
    backgroundColor: 'rgba(242, 95, 92, 0.15)',
    color: colors.danger,
    borderColor: 'transparent',
  },
  accent: {
    backgroundColor: 'rgba(201, 164, 107, 0.15)',
    color: colors.accent,
    borderColor: 'transparent',
  },
};

const sizeStyles = {
  sm: {
    height: '22px',
    fontSize: '11px',
    padding: `0 ${spacing[2]}`,
  },
  md: {
    height: '26px',
    fontSize: '12px',
    padding: `0 ${spacing[3]}`,
  },
};

export function AppPill({ variant = 'default', size = 'md', sx, ...props }: AppPillProps) {
  return (
    <Chip
      sx={{
        ...variantStyles[variant],
        ...sizeStyles[size],
        borderRadius: radius.sm,
        fontFamily: fonts.body,
        fontWeight: 500,
        border: `1px solid ${variantStyles[variant].borderColor}`,
        '& .MuiChip-label': {
          padding: 0,
        },
        ...sx,
      }}
      {...props}
    />
  );
}

// Status pill with predefined status mappings
type StatusType =
  | 'scheduled'
  | 'confirmed'
  | 'completed'
  | 'cancelled'
  | 'no_show'
  | 'active'
  | 'inactive'
  // Provider / invite statuses
  | 'pending'
  | 'approved'
  | 'suspended'
  | 'revoked'
  | 'accepted';

const statusConfig: Record<StatusType, { variant: AppPillVariant; label: string }> = {
  scheduled: { variant: 'default', label: 'Scheduled' },
  confirmed: { variant: 'accent', label: 'Confirmed' },
  completed: { variant: 'success', label: 'Completed' },
  cancelled: { variant: 'danger', label: 'Cancelled' },
  no_show: { variant: 'warning', label: 'No Show' },
  active: { variant: 'success', label: 'Active' },
  inactive: { variant: 'default', label: 'Inactive' },
  // Provider / invite statuses
  pending: { variant: 'warning', label: 'Pending' },
  approved: { variant: 'success', label: 'Approved' },
  suspended: { variant: 'danger', label: 'Suspended' },
  revoked: { variant: 'danger', label: 'Revoked' },
  accepted: { variant: 'success', label: 'Accepted' },
};

interface AppStatusPillProps {
  status: StatusType;
  size?: AppPillSize;
}

export function AppStatusPill({ status, size = 'sm' }: AppStatusPillProps) {
  const config = statusConfig[status] || { variant: 'default', label: status };
  return <AppPill variant={config.variant} size={size} label={config.label} />;
}
